import Navbar from "../components/NavbarM";
import React, { useState, useEffect, useRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import {
  Search,
  CreditCard,
  Store,
  ShieldCheck,
  User,
  Mail,
  Coins,
  CheckCircle,
  Keyboard,
  AlertTriangle,
  XCircle,
  RotateCcw,
} from "lucide-react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import "./CheckBalancePage.css";

function CheckBalancePage() {
  const [cardNumber, setCardNumber] = useState("");
  const [memberData, setMemberData] = useState(null);
  const [status, setStatus] = useState("idle");
  // idle | found | invalidCard | notActivated | noStoreBalance | blockedCard
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);

  const storeId = localStorage.getItem("storeId");
  const storeUid = localStorage.getItem("storeUid");
  const storeName = localStorage.getItem("storeName");

  const extractCardNumber = (raw) => {
    const value = String(raw || "").trim();

    if (value.includes("=")) {
      return value.split("=")[0].replace(/\D/g, "").trim();
    }

    const track2Match = value.match(/;?(\d{16})=/);
    if (track2Match) return track2Match[1];

    const track1Match = value.match(/B?(\d{16})\^/);
    if (track1Match) return track1Match[1];

    const digitsOnly = value.replace(/\D/g, "");

    if (digitsOnly.length >= 16) {
      return digitsOnly.slice(0, 16);
    }

    return digitsOnly;
  };

  const maskCard = (card) => {
    if (!card) return "";
    return String(card).replace(/^(\d{4})\d+(\d{4})$/, "$1********$2");
  };

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    let buffer = "";
    let reading = false;

    const handleKeyDown = (e) => {
      const key = e.key;

      if (key === ";") {
        reading = true;
        buffer = key;
        return;
      }

      if (reading) {
        buffer += key;

        if (key === "?") {
          reading = false;

          const displayValue = buffer.replace(/^;/, "").replace(/\?$/, "");
          setCardNumber(displayValue);
          handleSearch(displayValue);

          buffer = "";
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line
  }, []);

  const handleSearch = async (card = cardNumber) => {
    const cleanCard = extractCardNumber(card);

    if (!cleanCard || cleanCard.length < 16) {
      alert("Please enter or swipe a valid card number.");
      return;
    }

    if (!storeId || !storeUid) {
      alert("❌ Store not logged in. Please sign in again.");
      return;
    }

    try {
      setLoading(true);
      setMemberData(null);
      setStatus("idle");

      const cardRef = doc(db, "cards", cleanCard);
      const cardSnap = await getDoc(cardRef);

      // Random cards stop here
      if (!cardSnap.exists()) {
        setStatus("invalidCard");
        return;
      }

      const cardData = cardSnap.data();

      if (cardData.status === "blocked" || cardData.status === "lost") {
        setStatus("blockedCard");
        return;
      }

      if (cardData.activated !== true && cardData.status !== "activated") {
        setStatus("notActivated");
        return;
      }

      const storeMemberId = `${storeId}_${cleanCard}`;
      const storeMemberRef = doc(db, "storeMembers", storeMemberId);
      const storeMemberSnap = await getDoc(storeMemberRef);

      if (!storeMemberSnap.exists()) {
        setStatus("noStoreBalance");
        return;
      }

      setMemberData({
        ...storeMemberSnap.data(),
        cardNumber: cleanCard,
      });

      setStatus("found");
    } catch (err) {
      console.error(err);
      alert("Something went wrong while fetching data.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setCardNumber("");
    setMemberData(null);
    setStatus("idle");
    if (inputRef.current) inputRef.current.focus();
  };

  const cleanPreview = extractCardNumber(cardNumber);

  return (
    <>
      <Navbar />

      <div className="balance-page">
        <div className="balance-container">
          <section className="balance-hero">
            <div>
              <div className="balance-pill">
                <ShieldCheck size={18} />
                Secure Balance Lookup
              </div>

              <h1>Check Card Balance</h1>
              <p>
                Swipe or enter a PointsHub card to view the customer’s balance
                for this store only.
              </p>
            </div>

            <div className="balance-store-card">
              <Store size={22} />
              <div>
                <span>Active Store</span>
                <strong>{storeName || "Store"}</strong>
              </div>
            </div>
          </section>

          <section className="balance-grid">
            <div className="balance-search-panel">
              <div className="balance-terminal-visual">
                <div className="balance-card-icon">
                  <CreditCard size={48} />
                </div>
                <div className="balance-scan-line"></div>
              </div>

              <h3>Card Lookup Terminal</h3>
              <p>
                Swipe the card using your reader or enter the card number
                manually.
              </p>

              <div className="balance-input-label">Card Number</div>

              <div className="balance-input-box">
                <CreditCard size={20} />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Swipe or enter card number"
                  value={cardNumber}
                  onChange={(e) => {
                    setCardNumber(e.target.value);
                    setMemberData(null);
                    setStatus("idle");
                  }}
                />
              </div>

              {cardNumber && (
                <div className="balance-preview">
                  <span>Detected Card</span>
                  <strong>{cleanPreview || "Invalid card"}</strong>
                </div>
              )}

              <div className="balance-actions">
                <Button
                  className="balance-outline-btn"
                  onClick={() => inputRef.current?.focus()}
                >
                  <Keyboard size={18} />
                  Enter Manually
                </Button>

                <Button
                  className="balance-main-btn"
                  disabled={loading || !cardNumber}
                  onClick={() => handleSearch()}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Search size={18} />
                      Check Balance
                    </>
                  )}
                </Button>
              </div>

              <button className="balance-clear-btn" onClick={handleClear}>
                <RotateCcw size={16} />
                Clear Search
              </button>
            </div>

            <div className="balance-result-panel">
              {status === "idle" && (
                <div className="balance-empty">
                  <div className="balance-empty-icon">
                    <Search size={42} />
                  </div>
                  <h3>No Card Loaded</h3>
                  <p>
                    Swipe a card or enter a card number to see the balance
                    result here.
                  </p>
                </div>
              )}

              {status === "invalidCard" && (
                <div className="balance-status-card error">
                  <div className="status-icon error">
                    <XCircle size={42} />
                  </div>
                  <h3>Invalid PointsHub Card</h3>
                  <p>
                    This card number was not created from your admin card
                    inventory. Random cards cannot be used.
                  </p>
                </div>
              )}

              {status === "blockedCard" && (
                <div className="balance-status-card error">
                  <div className="status-icon error">
                    <XCircle size={42} />
                  </div>
                  <h3>Card Blocked</h3>
                  <p>
                    This card is blocked or marked as lost. Please contact
                    PointsHub admin.
                  </p>
                </div>
              )}

              {status === "notActivated" && (
                <div className="balance-status-card warning">
                  <div className="status-icon warning">
                    <AlertTriangle size={42} />
                  </div>
                  <h3>Card Not Activated</h3>
                  <p>
                    This card exists in PointsHub inventory, but it has not been
                    activated yet. Please add the member first.
                  </p>
                </div>
              )}

              {status === "noStoreBalance" && (
                <div className="balance-status-card warning">
                  <div className="status-icon warning">
                    <AlertTriangle size={42} />
                  </div>
                  <h3>No Balance In This Store</h3>
                  <p>
                    This card is active globally, but it has not started earning
                    points in this store yet.
                  </p>
                </div>
              )}

              {status === "found" && memberData && (
                <>
                  <div className="balance-found-badge">
                    <CheckCircle size={20} />
                    Balance Found
                  </div>

                  <div className="balance-points-circle">
                    <span>{memberData.points}</span>
                    <p>pts</p>
                  </div>

                  <h3 className="balance-title">Current Store Balance</h3>
                  <p className="balance-subtitle">
                    This balance is separate from other stores.
                  </p>

                  <div className="balance-detail-list">
                    <div className="balance-detail-row">
                      <div className="balance-detail-icon">
                        <User size={20} />
                      </div>
                      <div>
                        <span>Member Name</span>
                        <strong>{memberData.name || "Guest"}</strong>
                      </div>
                    </div>

                    <div className="balance-detail-row">
                      <div className="balance-detail-icon">
                        <Mail size={20} />
                      </div>
                      <div>
                        <span>Email</span>
                        <strong>{memberData.email || "Not Provided"}</strong>
                      </div>
                    </div>

                    <div className="balance-detail-row">
                      <div className="balance-detail-icon">
                        <CreditCard size={20} />
                      </div>
                      <div>
                        <span>Card Number</span>
                        <strong>{maskCard(memberData.cardNumber)}</strong>
                      </div>
                    </div>

                    <div className="balance-detail-row highlight">
                      <div className="balance-detail-icon coins">
                        <Coins size={20} />
                      </div>
                      <div>
                        <span>Available Points</span>
                        <strong>{memberData.points} pts</strong>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default CheckBalancePage;